import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { UNAPPROVED_EVENTS_QUERY, SHIPMENT_METRICS_QUERY } from '../../services/graphql/queries.js';
import { APPROVE_AND_UPDATE_EVENT_MUTATION, DELETE_SHIPMENT_EVENT_MUTATION } from '../../services/graphql/mutations.js';
import ShipmentEventsModal from './ShipmentEventsModal.jsx';
import ApprovalModal from './ApprovalModal.jsx';
import DeleteEventModal from './DeleteEventModal.jsx';
import { 
  AlertTriangle, 
  Clock, 
  Package, 
  CheckCircle,
  XCircle,
  Eye,
  Calendar,
  User,
  MapPin,
  Trash2
} from 'lucide-react';

const UnapprovedEventsTable = ({ userRole, user }) => {
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [selectedEventForDelete, setSelectedEventForDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { data, loading, error, refetch } = useQuery(UNAPPROVED_EVENTS_QUERY, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network'
  });

  const [approveAndUpdateEvent, { loading: approving }] = useMutation(APPROVE_AND_UPDATE_EVENT_MUTATION, {
    onCompleted: () => {
      toast.success('Evento aprobado y correos enviados a los destinatarios seleccionados');
      refetch();
      setIsApprovalModalOpen(false);
      setSelectedEvent(null);
    },
    onError: (error) => {
      toast.error(error.message || 'Error al aprobar el evento');
    },
    refetchQueries: [
      { query: UNAPPROVED_EVENTS_QUERY },
      { query: SHIPMENT_METRICS_QUERY }
    ],
    awaitRefetchQueries: true
  });

  const [deleteShipmentEvent, { loading: deleting }] = useMutation(DELETE_SHIPMENT_EVENT_MUTATION, {
    onCompleted: () => {
      toast.success('Evento eliminado exitosamente');
      refetch();
      setIsDeleteModalOpen(false);
      setSelectedEventForDelete(null);
    },
    onError: (error) => {
      toast.error(error.message || 'Error al eliminar el evento');
    },
    refetchQueries: [
      { query: UNAPPROVED_EVENTS_QUERY },
      { query: SHIPMENT_METRICS_QUERY }
    ],
    awaitRefetchQueries: true
  });

  const unapprovedEvents = data?.unapprovedEvents || [];

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleApprove = (event) => {
    setSelectedEvent(event);
    setIsApprovalModalOpen(true);
  };

  const handleApprovalConfirm = async ({ eventId, statusDetail, selectedRecipientsByGroup }) => {
    try {
      await approveAndUpdateEvent({ 
        variables: { 
          id: eventId, 
          statusDetail: statusDetail,
          selectedRecipientsByGroup: selectedRecipientsByGroup
        } 
      });
    } catch {
      // El toast de error ya lo muestra el onError de la mutation
    }
  };

  // Modal handlers
  const handleViewShipment = (shipment) => {
    // Normalize shipment data to match the format expected by ShipmentEventsModal
    const normalizedShipment = {
      manifest: shipment.manifest,
      reference: shipment.reference,
      inTransit: shipment.in_transit,
      createdAt: shipment.created_at,
      deliveredAt: shipment.delivered_at,
      notes: shipment.notes,
      origin: { 
        originName: shipment.origin?.origin_name 
      },
      destination: { 
        destinationName: shipment.destination?.destination_name 
      },
      loggedUser: {
        id: shipment.loggedUser?.id,
        fullName: shipment.loggedUser?.full_name
      },
      events: shipment.events || []
    };
    
    setSelectedShipment(normalizedShipment);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedShipment(null);
  };

  const handleCloseApprovalModal = () => {
    setIsApprovalModalOpen(false);
    setSelectedEvent(null);
  };

  const handleDeleteEvent = (event) => {
    setSelectedEventForDelete(event);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async (eventId) => {
    try {
      await deleteShipmentEvent({ 
        variables: { id: eventId } 
      });
    } catch {
      // El toast de error ya lo muestra el onError de la mutation
    }
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedEventForDelete(null);
  };

  if (!userRole || (userRole !== 'Administrador' && userRole !== 'Transportista')) {
    return (
      <div className="text-center py-8">
        <XCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">No tienes permisos para ver estados pendientes</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 animate-pulse">
            <div className="flex items-center space-x-4">
              <div className="h-10 w-10 bg-gray-300 rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                <div className="h-3 bg-gray-300 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <XCircle className="h-5 w-5 text-red-500 mr-2" />
          <p className="text-red-800">Error al cargar estados pendientes: {error.message}</p>
        </div>
      </div>
    );
  }

  if (unapprovedEvents.length === 0) {
    return (
      <div className="text-center py-12">
        <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
        <p className="text-gray-500 text-lg">¡Todo al día!</p>
        <p className="text-gray-400 text-sm mt-1">
          No hay estados pendientes de aprobación
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2" />
          <p className="text-yellow-800">
            Hay {unapprovedEvents.length} estado{unapprovedEvents.length !== 1 ? 's' : ''} pendiente{unapprovedEvents.length !== 1 ? 's' : ''} de aprobación
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {unapprovedEvents.map((event) => (
          <div key={event.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="bg-yellow-100 rounded-full p-2">
                    <Clock className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {event.status_detail}
                    </h3>
                    <p className="text-sm text-gray-500">
                      Estado creado el {formatDate(event.created_at)}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="flex items-center space-x-2">
                    <Package className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Manifiesto</p>
                      <p className="text-sm font-medium text-gray-900">
                        {event.shipment.manifest}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Ruta</p>
                      <p className="text-sm font-medium text-gray-900">
                        {event.shipment.origin.origin_name} → {event.shipment.destination.destination_name}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Responsable</p>
                      <p className="text-sm font-medium text-gray-900">
                        {event.shipment.loggedUser.full_name}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <p className="text-sm text-gray-600">
                    Fecha del estado: {formatDate(event.status_date)}
                  </p>
                </div>
              </div>

              <div className="flex flex-col space-y-2 ml-6">
                {userRole === 'Administrador' && (
                  <>
                    <button 
                      onClick={() => handleApprove(event)}
                      disabled={approving}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {approving ? (
                        <div className="h-4 w-4 mr-1 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-1" />
                      )}
                      {approving ? 'Aprobando...' : 'Aprobar'}
                    </button>
                    
                    <button 
                      onClick={() => handleDeleteEvent(event)}
                      disabled={deleting}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {deleting ? (
                        <div className="h-4 w-4 mr-1 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <Trash2 className="h-4 w-4 mr-1" />
                      )}
                      {deleting ? 'Eliminando...' : 'Eliminar Evento'}
                    </button>
                  </>
                )}
                
                <button 
                  onClick={() => handleViewShipment(event.shipment)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  Ver Detalle
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Shipment Events Modal */}
      <ShipmentEventsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        shipment={selectedShipment}
        userRole={userRole}
      />

      {/* Approval Modal */}
      <ApprovalModal
        isOpen={isApprovalModalOpen}
        onClose={handleCloseApprovalModal}
        event={selectedEvent}
        onConfirm={handleApprovalConfirm}
        user={user}
      />

      {/* Delete Event Modal */}
      <DeleteEventModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        event={selectedEventForDelete}
        onConfirm={handleDeleteConfirm}
        isLoading={deleting}
      />
    </div>
  );
};

export default UnapprovedEventsTable;